import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import type { HierarchyScope } from "./api";
import { openLocationCreator, type LocationCreator, type LocationSelection, type LocationSnapshot } from "./create-location";
import { hierarchyUrl } from "./urls";

export function CreateLocation({ selection, identity }: { selection: LocationSelection; identity: HierarchyScope }) {
  const kind = selection.workspaceId ? "project" : "workspace";
  const key = JSON.stringify([selection.accountId, selection.workspaceId, identity.userId]);
  const [current, setCurrent] = useState<{ key: string; creator: LocationCreator; value: LocationSnapshot }>();
  const [name, setName] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  const lifetime = useRef<AbortController | undefined>(undefined);
  const writes = useRef<Promise<LocationSnapshot> | undefined>(undefined);
  const inFlight = useRef(false), serial = useRef(0);
  useEffect(() => {
    const controller = new AbortController(); lifetime.current = controller;
    setCurrent(undefined); setError(false); setSaveFailed(false); setBusy(false);
    writes.current = undefined; inFlight.current = false; serial.current++;
    void openLocationCreator(selection, identity).then(async creator => {
      const value = await creator.read(controller.signal);
      if (!controller.signal.aborted) { setCurrent({ key, creator, value }); setName(value.name); }
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [key, revision]);
  const active = current?.key === key ? current : undefined;
  function edit(value: string) {
    if (!active || inFlight.current) return;
    setName(value); const edit = ++serial.current, signal = lifetime.current!.signal;
    const write = active.creator.saveName(value, signal); writes.current = write;
    void write.then(snapshot => {
      if (!signal.aborted && edit === serial.current) { setCurrent({ ...active, value: snapshot }); setError(false); setSaveFailed(false); }
    }, () => { if (!signal.aborted && edit === serial.current) { setError(true); setSaveFailed(true); } });
  }
  async function create(another = false) {
    if (!active || inFlight.current) return;
    const signal = lifetime.current!.signal;
    inFlight.current = true; setBusy(true); setError(false);
    try {
      await writes.current;
      const value = another ? await active.creator.startAnother(signal) : await active.creator.create(name, signal);
      if (!signal.aborted) { setCurrent({ ...active, value }); setName(value.name); }
    } catch {
      if (!signal.aborted) {
        setError(true);
        try {
          const value = await active.creator.read(signal);
          if (!signal.aborted) { setCurrent({ ...active, value }); setName(value.name); }
        } catch { if (!signal.aborted) setCurrent(undefined); }
      }
    } finally { if (!signal.aborted) { inFlight.current = false; setBusy(false); } }
  }
  const created = active?.value.created;
  return <div className="my-6 max-w-xl text-sm text-[#52606e]" aria-label={`Create a ${kind}`}>
    {active?.value.unavailableCreatedId ? <div>
      <p>The previous {kind} is no longer available here.</p>
      <Button className="mt-2" variant="ghost" disabled={busy} onClick={() => void create(true)}>Create another {kind}</Button>
    </div> : created ? <div className="flex flex-wrap items-center gap-4">
      <a className="font-medium text-[#20272b] underline" href={selection.workspaceId ? hierarchyUrl(selection.accountId, selection.workspaceId, created.id) : hierarchyUrl(selection.accountId, created.id)}>Open {created.name}</a>
      <Button variant="ghost" disabled={busy} onClick={() => void create(true)}>Create another {kind}</Button>
    </div> : active ? <form onSubmit={event => { event.preventDefault(); if (!saveFailed && name.trim()) void create(); }}>
      <label className="mb-2 block font-medium text-[#20272b]" htmlFor={`new-${kind}-name`}>New {kind}</label>
      <div className="flex flex-wrap gap-2">
        <input autoFocus={window.location.hash === `#create-${kind}`} id={`new-${kind}-name`} value={name} onChange={event => edit(event.target.value)} disabled={busy || Boolean(active.value.pending)} placeholder={kind === "workspace" ? "Workspace name" : "Project name"} className="min-w-0 flex-1 rounded-lg border border-[#cfe4f1] bg-white px-3 py-2 text-[#20272b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]" />
        <Button type="submit" disabled={busy || saveFailed || !name.trim()}>{active.value.pending ? "Retry creation" : `Create ${kind}`}</Button>
      </div>
    </form> : !error ? <p role="status">Loading saved {kind}…</p> : null}
    {busy ? <p className="mt-2" role="status">Saving {kind}…</p> : null}
    {error ? <div className="mt-2"><p role="alert">{saveFailed ? "This name could not be saved. Try saving it again." : `Creation could not be confirmed. Reload the saved ${kind} or retry it.`}</p>
      {saveFailed ? <Button variant="ghost" disabled={busy} onClick={() => edit(name)}>Save name</Button> : null}
      <Button variant="ghost" disabled={busy} onClick={() => setRevision(value => value + 1)}>Reload saved {kind}</Button>
    </div> : null}
  </div>;
}
